import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AgricultureMapConstants, LAYER_TYPES } from 'src/app/main/constants/dims-map-constants';
import { isNullOrUndefined } from 'util';
import { DrySpellDataService } from '../dry-spell-data.service';
import { SplitMapLayerService } from '../../split-screen/split-screen-dashboard/services/split-map-layer.service';
import {
  DRY_SPELL_LAYER_LIST,
  DRY_SPELL_LEGEND_LIST,
  DRY_SPELL_WMTS_BASE_URL,
  MONSOON_BLOCKS,
  SEASON_WINDOWS,
  YEAR_RANGE
} from './dry-spell-map-constants';

@Injectable({
  providedIn: 'root'
})
export class DrySpellMapService {

  gisMapComp: any;
  activeGroup: any;

  selectedYear: number = YEAR_RANGE.max;
  selectedThresh: number = 14;
  selectedMode: string = 'season';

  heatmapImages: { [windowKey: string]: { base64Image: string; bbox: number[][] } } = {};
  overlayLayers: any[] = [];

  heatmapImages$ = new BehaviorSubject<{ [key: string]: string }>({});
  loadingHeatmaps$ = new BehaviorSubject<boolean>(false);

  // Static WMS mask overlay (HEATWAVE:Dryspell_Agri_Mask) — one active layer per map index,
  // matching the leafletMaps/overlays/searchMarkers array convention in DrySpellMapComponent.
  private agriMaskOverlays: any[] = [null, null, null, null];
  private boundaryLayers: any[][] = [];
  // Keyed by map instance (not array index) because fitToSelectionBoundary() is sometimes
  // called with the full 4-map array and sometimes with just the fullscreen map — an index-based
  // array would misalign/corrupt entries across calls with different subsets.
  private selectionGeoJsonLayers = new Map<any, any>();
  private selectionSeq = 0;
  private districtLabelLayers: any[] = [];

  constructor(
    private dataSrv: DrySpellDataService,
    private splitLayerService: SplitMapLayerService
  ) {}

  /** Removes every currently tracked boundary layer from its map, before the next level is applied. */
  private clearBoundaryLayers(maps: any[]) {
    this.boundaryLayers.forEach((layers, idx) => {
      const map = maps[idx];
      if (!map || !layers) { return; }
      layers.forEach(layer => {
        if (layer && map.hasLayer(layer)) { map.removeLayer(layer); }
      });
    });
    this.boundaryLayers = [];
  }

  /** Removes the per-map selection GeoJSON layer used only to compute fitBounds(), mirroring SplitMapCardComponent.removeSelectionLayers(). */
  private clearSelectionGeoJson(maps: any[]) {
    maps.forEach(map => {
      const layer = this.selectionGeoJsonLayers.get(map);
      if (layer && map && map.hasLayer(layer)) { map.removeLayer(layer); }
      this.selectionGeoJsonLayers.delete(map);
    });
  }

  /** Country level: unfiltered State boundary on every map (reuses SplitMapLayerService, the Split Screen boundary logic). */
  applyStateLevelBoundary(maps: any[]) {
    this.selectionSeq++;
    this.clearSelectionGeoJson(maps);
    this.clearBoundaryLayers(maps);
    maps.forEach((map, idx) => {
      if (!map) { return; }
      this.boundaryLayers[idx] = this.splitLayerService.addBoundaryLayers(map) || [];
    });
  }

  /** State drill-down: District boundary filtered to the selected state, on every map. */
  applyDistrictLevelBoundary(maps: any[], stateUUID: string) {
    this.clearBoundaryLayers(maps);
    maps.forEach((map, idx) => {
      if (!map) { return; }
      const layer = this.splitLayerService.addFilteredDistrictVectorLayer(map, stateUUID);
      this.boundaryLayers[idx] = layer ? [layer] : [];
    });
  }

  /** District drill-down: Block boundary filtered to the selected district, on every map. */
  applyBlockLevelBoundary(maps: any[], distUUID: string) {
    this.clearBoundaryLayers(maps);
    maps.forEach((map, idx) => {
      if (!map) { return; }
      const layer = this.splitLayerService.addFilteredBlockVectorLayer(map, distUUID);
      this.boundaryLayers[idx] = layer ? [layer] : [];
    });
  }

  /**
   * Fits every given map to the selected State/District's real boundary geometry, exactly like
   * SplitMapCardComponent.updateSelectionLayers(): fetch the admin polygon (cached WFS GeoJSON,
   * shared across every caller), then map.fitBounds(layer.getBounds(), { padding: [5, 5] }).
   * A sequence guard discards the fit if a newer selection superseded this one mid-fetch.
   *
   * `maps` may be the full 4-view array or just a single fullscreen map — fitBounds() recomputes
   * the zoom from each map's own current container size, so the same call naturally yields an
   * independent, appropriately-zoomed result for the 4-view grid vs. the fullscreen container.
   */
  private async fitToSelectionBoundary(maps: any[], locType: 'STATE' | 'DISTRICT', uuid: string) {
    this.clearSelectionGeoJson(maps);
    const seq = ++this.selectionSeq;

    await Promise.all(maps.map(async (map) => {
      if (!map) { return; }
      try {
        const layer = await this.splitLayerService.addSelectionGeoJSONLayer(map, locType, uuid);
        if (seq !== this.selectionSeq) {
          if (layer && map.hasLayer(layer)) { map.removeLayer(layer); }
          return;
        }
        this.selectionGeoJsonLayers.set(map, layer);
        if (layer) {
          const bounds = (layer as any).getBounds();
          if (bounds && bounds.isValid()) { map.fitBounds(bounds, { padding: [5, 5] }); }
        }
      } catch (e) {
        console.warn('DrySpellMapService: fitToSelectionBoundary failed', e);
      }
    }));
  }

  /** Fits the given map(s) to the selected State's real bounds (State drill-down). */
  fitToStateBoundary(maps: any[], stateUUID: string) {
    return this.fitToSelectionBoundary(maps, 'STATE', stateUUID);
  }

  /** Fits the given map(s) to the selected District's real bounds (District drill-down / block level). */
  fitToDistrictBoundary(maps: any[], distUUID: string) {
    return this.fitToSelectionBoundary(maps, 'DISTRICT', distUUID);
  }

  /** Removes the per-map district name label layer, mirroring SplitMapCardComponent.removeDistrictLabelLayer(). */
  clearDistrictLabels(maps: any[]) {
    this.districtLabelLayers.forEach((layer, idx) => {
      const map = maps[idx];
      if (layer && map && map.hasLayer(layer)) { map.removeLayer(layer); }
    });
    this.districtLabelLayers = [];
  }

  /**
   * Adds the selected District's name label (WMS, filtered to distUUID) on every map,
   * exactly like SplitMapCardComponent.updateDistrictLabelLayer() → SplitMapLayerService.addDistrictLabelLayer().
   * Only ever called once a specific District is selected — matches Split Screen's own trigger point.
   */
  addDistrictLabels(maps: any[], distUUID: string) {
    this.clearDistrictLabels(maps);
    maps.forEach((map, idx) => {
      if (!map) { return; }
      this.districtLabelLayers[idx] = this.splitLayerService.addDistrictLabelLayer(map, distUUID);
    });
  }

  /**
   * GeoWebCache WMTS GetTile URL for the static HEATWAVE:Dryspell_Agri_Mask layer — same
   * gwc/service/wmts endpoint/tiling scheme as buildWmtsTileUrl() (ds14/ds21), just without a
   * cql_filter since this layer isn't parameterized by year/season. Using WMTS instead of a
   * classic WMS GetMap request avoids that endpoint's 504 Gateway Timeout for this layer.
   */
  private buildAgriMaskWmtsTileUrl(): string {
    return DRY_SPELL_WMTS_BASE_URL
      + 'gwc/service/wmts?layer=HEATWAVE:Dryspell_Agri_Mask'
      + '&tilematrixset=EPSG:900913&Service=WMTS&Request=GetTile&Version=1.0.0'
      + '&Format=image/png'
      + '&TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}';
  }

  /**
   * Adds/removes the static HEATWAVE:Dryspell_Agri_Mask WMTS overlay so that only the map at
   * `eligibleIndex` shows it (pass -1 when nothing currently qualifies, e.g. Agri Mask is off,
   * the year isn't 2025, or the season window isn't the 4th/June–September one — see
   * DrySpellMapComponent.getAgriMaskEligibleMapIndex()). Every other index has its overlay
   * removed if present, so switching year/window/mode never leaves a stale mask on a map that no
   * longer qualifies. The underlying ds14/ds21 heatmap tile is left untouched either way — the
   * mask renders on top via a dedicated 'agriMaskPane' (z-index above drySpellPane's 400).
   * Mirrors the remove-old-layer-before-adding-new pattern used by Fog's setWMSOverlays()
   * (gisdata.service.ts), keyed here by map index instead of a layer-list name since Dry Spell's
   * grid tracks layers by fixed array position (see `overlays` in DrySpellMapComponent). Never
   * duplicates an already-present layer — safe to call on every drill-down/year/duration/window/
   * mode/play-bar refresh.
   */
  updateAgriMaskOverlay(maps: any[], eligibleIndex: number) {
    const L = (window as any).L;
    maps.forEach((map, idx) => {
      if (idx !== eligibleIndex) {
        const layer = this.agriMaskOverlays[idx];
        if (map && layer) {
          try { if (map.hasLayer(layer)) { map.removeLayer(layer); } } catch (_) {}
        }
        this.agriMaskOverlays[idx] = null;
        return;
      }
      if (!map || this.agriMaskOverlays[idx] || !L) { return; }
      if (!map.getPane('agriMaskPane')) {
        const pane = map.createPane('agriMaskPane');
        pane.style.zIndex = '450';
        pane.style.pointerEvents = 'none';
      }
      const layer = L.tileLayer(this.buildAgriMaskWmtsTileUrl(), {
        opacity: 0.85,
        pane: 'agriMaskPane',
        maxZoom: 12,
        minZoom: 4
      });
      layer.addTo(map);
      this.agriMaskOverlays[idx] = layer;
    });
  }

  /**
   * Clears tracked Agri Mask overlay references without touching any Leaflet map — for when the
   * maps themselves are already being torn down (DrySpellMapComponent.ngOnDestroy()) or fresh
   * ones are about to be created (initMaps()), so calling removeLayer() on an already-destroyed
   * map is unnecessary/unsafe. This service is `providedIn: 'root'`, so `agriMaskOverlays`
   * otherwise survives route navigation away from and back to the module: updateAgriMaskOverlay()'s
   * `if (this.agriMaskOverlays[idx]) return;` guard would then mistake those dangling references
   * (from the previous, now-destroyed map instances) for "already added to this map" and skip
   * adding the layer to the newly (re)created ones — reproducing exactly the bug where the Agri
   * Mask overlay is missing after leaving and returning to the module, until the eligible index is
   * revisited (which happens to reset this array as a side effect of updateAgriMaskOverlay()).
   */
  resetAgriMaskOverlayState() {
    this.agriMaskOverlays = [null, null, null, null];
  }

  getSrcWiseLayers(gisMapComp: any) {
    this.gisMapComp = gisMapComp;
    gisMapComp.legendList = {};
    gisMapComp.showThrobber = true;

    const queryParams: any = gisMapComp['params'];
    gisMapComp.getAndSetBaseLayers(2);

    if (!isNullOrUndefined(queryParams) && Object.keys(queryParams).length > 0) {
      const layerList: any = {};
      layerList['State Boundary'] = DRY_SPELL_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['State Boundary'];

      if (queryParams.locType === 'STATE' || queryParams.locType === 'DISTRICT') {
        layerList['District Boundary'] = DRY_SPELL_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA]['District Boundary'];
      }

      this.addSrcWiseVectorLayers(layerList, gisMapComp, queryParams);
      this.addSrcWiseWMSLayers(layerList, gisMapComp, queryParams);
      this.addLegend(gisMapComp);

      this.loadHeatmaps(gisMapComp);
    }

    gisMapComp.showThrobber = false;
  }

  buildWmtsTileUrl(year: number, thresh: number, seasonKey: string): string {
    const layerType = 'ds' + thresh;
    const layerName = 'dryspell:' + layerType;
    const cqlFilter = "location='" + layerType + '_' + year + '_' + seasonKey + ".tif'";
    return DRY_SPELL_WMTS_BASE_URL
      + 'gwc/service/wmts?layer=' + layerName
      + '&tilematrixset=EPSG:900913&Service=WMTS&Request=GetTile&Version=1.0.0'
      + '&Format=image/png'
      + '&TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}'
      + '&cql_filter=' + cqlFilter;
  }

  loadHeatmaps(gisMapComp?: any) {
    this.loadingHeatmaps$.next(true);
    this.clearOverlays();

    const L = (window as any).L;
    if (!L) {
      this.loadingHeatmaps$.next(false);
      return;
    }

    const mapObj = gisMapComp ? gisMapComp.map : null;
    if (mapObj && !mapObj.getPane('drySpellPane')) {
      const pane = mapObj.createPane('drySpellPane');
      pane.style.zIndex = '400';
      pane.style.pointerEvents = 'none';
    }

    for (const win of SEASON_WINDOWS) {
      const url = this.buildWmtsTileUrl(this.selectedYear, this.selectedThresh, win.key);
      if (mapObj) {
        const tileLayer = L.tileLayer(url, {
          opacity: 0.85,
          pane: mapObj.getPane('drySpellPane') ? 'drySpellPane' : 'overlayPane',
          maxZoom: 12,
          minZoom: 4
        });
        this.overlayLayers.push(tileLayer);
        tileLayer.addTo(mapObj);
      }
    }

    this.loadingHeatmaps$.next(false);
  }

  clearOverlays() {
    if (this.gisMapComp && this.gisMapComp.map) {
      this.overlayLayers.forEach(overlay => {
        if (this.gisMapComp.map.hasLayer(overlay)) {
          this.gisMapComp.map.removeLayer(overlay);
        }
      });
    }
    this.overlayLayers = [];
  }

  addSrcWiseVectorLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let vectorLayers = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.VECTOR_LAYER);
    vectorLayers = this.formatVectorLayersData(vectorLayers, queryParams);
    for (const key in vectorLayers) {
      gisMapComp.activeLayers[key] = vectorLayers[key].layerOptions
        ? vectorLayers[key].layerOptions.visible
        : true;
    }
    vectorLayers = this.applyBoundaryStyle(vectorLayers);
    gisMapComp.addSrcWiseVECTORLayers(vectorLayers);
  }

  formatVectorLayersData(layerList: any, queryParams: any) {
    for (const layerName in layerList) {
      const uuid = queryParams['pUUID'];
      if (queryParams['locType'] === 'STATE' && layerName === 'District Boundary') {
        layerList[layerName].layerOptions.cql_filter_value = 'state_uuid';
        layerList[layerName].layerOptions.cql_filter = `state_uuid='${uuid}'`;
      } else if (queryParams['locType'] === 'DISTRICT' && layerName === 'District Boundary') {
        layerList[layerName].layerOptions.cql_filter_value = 'dist_uuid';
        layerList[layerName].layerOptions.cql_filter = `dist_uuid='${uuid}'`;
      }
    }
    return layerList;
  }

  applyBoundaryStyle(layerList: any) {
    for (const key in layerList) {
      const layerName = layerList[key].layerOptions
        ? layerList[key].layerOptions.layerName
        : null;
      if (!layerName) continue;
      const lName = layerName.split(':')[1];
      if (!layerList[key].layerOptions.vectorTileLayerStyles) {
        layerList[key].layerOptions.vectorTileLayerStyles = {};
      }
      layerList[key].layerOptions.vectorTileLayerStyles[lName] = () => ({
        color: '#444444',
        fill: false,
        weight: 0.6,
        opacity: 0.7
      });
    }
    return layerList;
  }

  addSrcWiseWMSLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let wmsLayers = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.WMS_LAYER);
    for (const key in wmsLayers) {
      gisMapComp.activeLayers[key] = true;
    }
    gisMapComp.addSrcWiseWMSLayers(wmsLayers);
  }

  addLegend(gisMapComp: any) {
    const legends = DRY_SPELL_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA];
    for (const key in legends) {
      gisMapComp.legendList[key] = legends[key];
    }
    gisMapComp.manageLegend();
  }

  changeTimeLayers(gis: any, groupName: any) {
    gis.activeGroup = groupName;
    this.activeGroup = groupName;
  }

  onYearChange(year: number) {
    this.selectedYear = year;
    this.loadHeatmaps(this.gisMapComp);
  }

  onThresholdChange(thresh: number) {
    this.selectedThresh = thresh;
    this.loadHeatmaps(this.gisMapComp);
  }
}
